/** Pure helpers for the `project_start` → Creative Ideation →
 * `creative_candidate` branching flow (VISTOERA_CANVAS_PROJECT_START_
 * CREATIVE_BRIEF_AND_IDEATION_BRANCHING_NODE_V1). Kept out of
 * `CanvasBoard.tsx` so the graph-walking / payload-shaping logic is
 * independently testable (spec §81 "Frontend至少...auto placement /
 * select / expand / stale state").
 *
 * Architecture boundary (spec §1) preserved here too: these helpers
 * only ever read/write Canvas `Node`/`Edge` objects — the actual HTTP
 * dispatch (`generateCreativeIdeation`/`expandCreativeIdeation`) stays
 * in `CanvasBoard.tsx` next to every other API call it already owns. */

import type { Edge, Node } from '@xyflow/react'
import type { CreativeIdeationBriefPayload, CreativeIdeationCandidate } from '../../../api'
import type { FlowNodeData } from './types'

/** Canvas edge `relationType` linking a `project_start`/`creative_candidate`
 * node to each `creative_candidate` it produced (spec §33/§40 — "必须在
 * Canvas建立明确 edges" / "parent_candidate_id / root_brief_id"). Reuses
 * the existing `CanvasRelationType` union value `GENERATED_FROM` rather
 * than inventing a new one. */
export const CREATIVE_CANDIDATE_EDGE_RELATION = 'GENERATED_FROM'

const BRIEF_AFFECTING_FIELD_KEYS = [
  'userIntent',
  'contentGoal',
  'targetDurationSeconds',
  'assets',
  'mustInclude',
  'mustAvoid',
  'audience',
  'platform',
  'tone',
  'references',
  'skillSelectionSource',
  'skillKey',
  'providerSelectionMode',
  'providerOptionId',
] as const

/** Backend `_build_skill_input` only reads these keys off
 * `parent_candidate` (see `creative_ideation_service.py`) — trims the
 * rest so the `/expand` payload stays a light, intentional snapshot
 * rather than the whole candidate node's `data.fields` bag. */
export function buildParentCandidatePayload(fields: Record<string, unknown>): {
  candidate_id: string
  title: string
  premise: string
  hook: string
  tone: string
  story_direction: string
} {
  return {
    candidate_id: String(fields.candidateId ?? ''),
    title: String(fields.title ?? ''),
    premise: String(fields.premise ?? ''),
    hook: String(fields.hook ?? ''),
    tone: String(fields.tone ?? ''),
    story_direction: String(fields.storyDirection ?? ''),
  }
}

/** spec §42/§43 — only THESE fields (the ones that actually change what
 * the AI would generate) should ever mark existing candidates stale or
 * bump `briefVersion`; internal run bookkeeping (`activeRunId`,
 * `runStatus`, `candidateCount`, `moreRequirementsExpanded`, …) must
 * never trigger it. */
export function isBriefAffectingPatch(patch: Record<string, unknown>): boolean {
  return BRIEF_AFFECTING_FIELD_KEYS.some((key) => key in patch)
}

function assetIdList(assets: unknown): string[] {
  if (!Array.isArray(assets)) return []
  return assets
    .map((a) => (a && typeof a === 'object' ? (a as Record<string, unknown>).asset_id ?? (a as Record<string, unknown>).assetId : null))
    .filter((v): v is string => typeof v === 'string' && v.length > 0)
}

/** Builds the `CreativeIdeationBriefPayload` sent to `/generate` and
 * `/expand` straight from a `project_start` node's own `data.fields` —
 * the ONLY place a Brief snapshot is assembled, so both call sites stay
 * byte-for-byte consistent (spec §29 "每次 Ideation Run必须 snapshot"). */
export function buildBriefPayload(fields: Record<string, unknown>): CreativeIdeationBriefPayload {
  const assets = Array.isArray(fields.assets) ? (fields.assets as Array<Record<string, unknown>>) : []
  return {
    user_intent: String(fields.userIntent ?? '').trim(),
    content_goal: (fields.contentGoal as string | null | undefined) ?? null,
    content_goal_source: (fields.contentGoalSource as string | null | undefined) ?? null,
    target_duration_seconds: (fields.targetDurationSeconds as number | null | undefined) ?? null,
    duration_source: (fields.durationSource as string | null | undefined) ?? null,
    assets: assets.length
      ? assets.map((a) => ({
          asset_id: (a.asset_id as string | null | undefined) ?? null,
          asset_type: String(a.asset_type ?? 'unknown'),
          display_name: String(a.display_name ?? a.asset_id ?? 'asset'),
        }))
      : null,
    must_include: Array.isArray(fields.mustInclude) && fields.mustInclude.length ? (fields.mustInclude as string[]) : null,
    must_avoid: Array.isArray(fields.mustAvoid) && fields.mustAvoid.length ? (fields.mustAvoid as string[]) : null,
    audience: (fields.audience as string | null | undefined) ?? null,
    platform: (fields.platform as string | null | undefined) ?? null,
    tone: (fields.tone as string | null | undefined) ?? null,
    references: Array.isArray(fields.references) && fields.references.length ? (fields.references as string[]) : null,
  }
}

/** Fingerprint of only the brief-affecting fields (mirrors the backend's
 * `compute_brief_fingerprint` shape closely enough for client-side
 * "did the brief change since the last run" comparisons — this never
 * needs to match the backend hash byte-for-byte, it only needs to be
 * stable for the SAME inputs on the client). */
export function briefFingerprint(fields: Record<string, unknown>): string {
  const payload = {
    intent: String(fields.userIntent ?? '').trim(),
    contentGoal: fields.contentGoal ?? null,
    duration: fields.targetDurationSeconds ?? null,
    assets: assetIdList(fields.assets).sort(),
    mustInclude: fields.mustInclude ?? [],
    mustAvoid: fields.mustAvoid ?? [],
    audience: fields.audience ?? null,
    platform: fields.platform ?? null,
    tone: fields.tone ?? null,
    skill: fields.skillSelectionSource === 'EXPLICIT' ? fields.skillKey ?? null : 'AUTO',
    provider: fields.providerSelectionMode === 'EXPLICIT' ? fields.providerOptionId ?? null : 'DEFAULT',
  }
  return JSON.stringify(payload)
}

/** Default `data.fields` for a brand-new `creative_candidate` Canvas
 * node materialized from one structured candidate the backend returned
 * (spec §25 output schema → §31 "每个方案一个节点"). */
export function buildCandidateNodeFields(
  candidate: CreativeIdeationCandidate,
  opts: {
    rootBriefNodeId: string
    parentCandidateNodeId: string | null
    generationDepth: number
    sourceRunId: string
    rootRunId: string
    briefFingerprintAtRun: string
  },
): Record<string, unknown> {
  return {
    candidateId: candidate.candidate_id,
    title: candidate.title,
    premise: candidate.premise,
    hook: candidate.hook,
    tone: candidate.tone,
    storyDirection: candidate.story_direction,
    productRole: candidate.product_role ?? null,
    characterRole: candidate.character_role ?? null,
    estimatedDuration: candidate.estimated_duration ?? null,
    tags: Array.isArray(candidate.tags) ? candidate.tags : [],
    rationale: candidate.rationale ?? '',
    selected: false,
    rejected: false,
    stale: false,
    rootBriefNodeId: opts.rootBriefNodeId,
    parentCandidateNodeId: opts.parentCandidateNodeId,
    generationDepth: opts.generationDepth,
    sourceRunId: opts.sourceRunId,
    rootRunId: opts.rootRunId,
    briefFingerprintAtRun: opts.briefFingerprintAtRun,
    activeExpandRunId: null,
    expandRunStatus: null,
    expandRunErrorMessage: null,
  }
}

/** Walks `GENERATED_FROM` edges from `rootNodeId` to collect every
 * `creative_candidate` descendant — direct Ideation children AND every
 * further "发散" grandchild (spec §40 "Branch Tree...必须保持 graph
 * relationship") — so Brief-change staleness (spec §42) reaches the
 * WHOLE branch, not just the first generation. */
export function collectDescendantCandidateIds(nodes: Node[], edges: Edge[], rootNodeId: string): Set<string> {
  const byId = new Map(nodes.map((n) => [n.id, n]))
  const childrenOf = new Map<string, string[]>()
  for (const e of edges) {
    if ((e.data as { relationType?: string } | undefined)?.relationType !== CREATIVE_CANDIDATE_EDGE_RELATION) continue
    const list = childrenOf.get(e.source) ?? []
    list.push(e.target)
    childrenOf.set(e.source, list)
  }
  const result = new Set<string>()
  const queue = [...(childrenOf.get(rootNodeId) ?? [])]
  while (queue.length) {
    const id = queue.shift() as string
    if (result.has(id)) continue
    const node = byId.get(id)
    const nodeType = (node?.data as unknown as FlowNodeData | undefined)?.nodeType
    if (nodeType !== 'creative_candidate') continue
    result.add(id)
    queue.push(...(childrenOf.get(id) ?? []))
  }
  return result
}

/** spec §42 "已有 Candidate不应消失，但应 mark stale relative to current
 * brief" — flips `fields.stale = true` on every candidate descendant of
 * `rootNodeId`, never removing/mutating any other field. No-op (same
 * array reference semantics as the rest of this file's callers expect)
 * when there is nothing downstream yet. */
export function markCandidatesStale(nodes: Node[], edges: Edge[], rootNodeId: string): Node[] {
  const staleIds = collectDescendantCandidateIds(nodes, edges, rootNodeId)
  if (staleIds.size === 0) return nodes
  return nodes.map((n) => {
    if (!staleIds.has(n.id)) return n
    const data = n.data as unknown as FlowNodeData
    if (data.fields?.stale) return n
    return {
      ...n,
      data: {
        ...data,
        fields: { ...data.fields, stale: true },
      } as unknown as Record<string, unknown>,
    }
  })
}
